import { Icon } from '@eti/components';
import { css, cx, mediaQueries } from '@eti/styles';
import { Stack } from '@etg/wings';
import {
  DIRECTIONS,
  useSiteContext,
  useDirection,
  useTranslation,
  useProperty,
} from '@eti/providers';
import { constants, duration as durationUtil } from '@eti/utils';
import type { TRAIN_SEGMENT_TYPE } from '../constants';
import { TRAIN_SEGMENTS } from '../constants';

const { LTR } = DIRECTIONS;
const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const wrapperStyles = css`
  flex: 1 0 20%;
`;

const textStyles = css`
  color: #737373;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }
`;

const centeredTextStyles = css`
  text-align: center;
`;

const stopDotStyles = css`
  background: #737373;
  border: 1px solid #737373;
  border-radius: 5px;
  bottom: 4.5px;
  content: '';
  display: inline-block;
  height: 8px;
  position: relative;
  width: 8px;
`;

const separatorStyles = (gapSize: number) => css`
  border-top: 1px solid #737373;
  display: flex;
  gap: ${gapSize}px;
  height: 1px;
  justify-content: center;
  padding: 0;
  position: relative;
  width: 100%;
`;

const tripIconWrapperStyles = css`
  position: relative;
  width: 100%;
`;

const arrowStyles = (direction: string) => css`
  color: #737373;
  inset-inline-end: -5px;
  position: absolute;
  top: -2.5px;
  transform: rotate(${direction === LTR ? 0 : '180deg'});
`;

interface TripDetailsDurationProps {
  boundNumber: number;
  duration: number;
  includesTrainSegments: TRAIN_SEGMENT_TYPE;
  numberOfTechnicalStops: number;
  stops: number;
}

export const TripDetailsDuration = ({
  boundNumber,
  duration,
  includesTrainSegments,
  numberOfTechnicalStops,
  stops,
}: TripDetailsDurationProps) => {
  const { direction } = useDirection();
  const { language, timeFormat } = useSiteContext();
  const { t } = useTranslation();
  const { p } = useProperty();
  const isDirectFlight = stops === 0;
  const stopText =
    stops === 1 ? t('TripDetails.Details.Stop') : t('TripDetails.Details.Stops', stops);
  const isTechnicalStopsEnabled = p('IbeClient.TripDetails.TechnicalStops.Enabled');
  const totalStops = isTechnicalStopsEnabled ? stops + numberOfTechnicalStops : stops;

  let gapSize: number;
  switch (true) {
    case totalStops < 5:
      gapSize = 25;
      break;
    case totalStops > 5:
      gapSize = 15;
      break;
    default:
      gapSize = 20;
  }

  return (
    <Stack align="center" className={wrapperStyles} spacing={8}>
      <p className={textStyles} data-testid={`trip-bound-${boundNumber}-info-duration`}>
        {`${LRE}${durationUtil.getDuration(
          duration,
          language.code,
          timeFormat,
          ['h', 'm', 's'],
          false,
        )}${PDF}`}
      </p>
      <div className={tripIconWrapperStyles}>
        <div className={separatorStyles(gapSize)} data-testid="trip-stop-dot-wrapper">
          {[...Array(totalStops).keys()].map((key) => (
            <div
              className={cx({
                [stopDotStyles]: totalStops > 0,
              })}
              data-testid={`trip-stop-dot-${key}`}
              key={key}
            />
          ))}
        </div>
        <Icon className={arrowStyles(direction)} color="#737373" id="bound-arrow" size="0.375rem" />
      </div>

      <Stack align="center" spacing={4}>
        <p className={textStyles} data-testid={`trip-bound-${boundNumber}-info-stops`}>
          {isDirectFlight ? t('TripDetails.Details.Direct') : stopText}
        </p>
        {includesTrainSegments === TRAIN_SEGMENTS.SOME && (
          <p className={textStyles} data-testid="trip-part-by-train-text">
            {t('IbeClient.Segment.PartByTrain.Text')}
          </p>
        )}
        {includesTrainSegments === TRAIN_SEGMENTS.ALL && (
          <p className={textStyles} data-testid="trip-by-train-text">
            {t('IbeClient.Segment.Train.Text')}
          </p>
        )}
        {numberOfTechnicalStops > 0 && (
          <p className={cx(textStyles, centeredTextStyles)} data-testid="trip-technical-stops">
            {t('TripDetails.Details.NumberOfTechnicalStops', numberOfTechnicalStops)}
          </p>
        )}
      </Stack>
    </Stack>
  );
};
